import React, { useState, useEffect } from 'react';
import { Table, Pagination, Select, Spin, Tag, Button } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { fetchUserOrders, getLabelZip, fetchSellers } from '../../services/user';
import ExportModal from '../../components/ExportModal';

interface Order {
  _id: string;
  orderNumber: string;
  orderId: string;
  customerName: string;
  totalAmount: number;
  createdAt: string;
  status: 'Pending' | 'In-progress' | 'Completed' | 'Cancelled';
  items: {
    productName: string;
    quantity: number;
    price: number;
  }[];
  logisticPayload: any;
}

interface NewOrder {
  _id: string;
  orderId: string;
  customerName: string;
  date: string;
  amount: number;
  status: 'Pending' | 'In-progress' | 'Completed' | 'Cancelled';
  shippingLabelUrl: string | null;
}

const SearchableSellerDropdown = ({ sellers, value, onChange }: { sellers: any[], value: string, onChange: (val: string) => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  
  const filtered = sellers.filter(s => 
    (s.businessName || s.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.email || '').toLowerCase().includes(search.toLowerCase())
  );
  
  const selectedSeller = sellers.find(s => s._id === value);
  const displayValue = value === 'all' ? 'All Sellers' : (selectedSeller ? `${selectedSeller.name || selectedSeller.email}` : 'Select Seller');

  return (
    <div className="relative z-50">
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md bg-white cursor-pointer relative"
      >
        <span className="block truncate">{displayValue}</span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </span>
      </div>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 w-full top-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl overflow-hidden">
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <input 
                type="text"
                autoFocus
                placeholder="Search sellers..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 shadow-inner"
              />
            </div>
            <ul className="max-h-60 overflow-auto py-1">
              <li 
                className={`px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm ${value === 'all' ? 'bg-blue-100 font-medium' : 'text-gray-700'}`}
                onClick={() => { onChange('all'); setIsOpen(false); setSearch(''); }}
              >
                All Sellers
              </li>
              {filtered.map(s => (
                <li 
                  key={s._id}
                  className={`px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm ${value === s._id ? 'bg-blue-100 font-medium' : 'text-gray-700'}`}
                  onClick={() => { onChange(s._id); setIsOpen(false); setSearch(''); }}
                >
                  {s.name || s.email}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

const OrderListPage: React.FC = () => {
  const [orders, setOrders] = useState<NewOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedOrder, setSelectedOrder] = useState<NewOrder[]>([]);
  
  // Filter States
  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRangeFilter, setDateRangeFilter] = useState<string>('All Time');
  const [priceRange, setPriceRange] = useState<number[]>([0, 10000]);
  const [selectedSeller, setSelectedSeller] = useState<string>('all');
  
  // Local Filter Modal States
  const [showFilters, setShowFilters] = useState(false);
  const [localStatus, setLocalStatus] = useState<string>('all');
  const [localDateRange, setLocalDateRange] = useState<string>('All Time');
  const [localPriceRange, setLocalPriceRange] = useState<number[]>([0, 10000]);
  const [localSeller, setLocalSeller] = useState<string>('all');

  const [role, setRole] = useState<string>('');
  const [orgSellers, setOrgSellers] = useState<any[]>([]);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  useEffect(() => {
    let userRole = '';
    try {
      const userObj = JSON.parse(localStorage.getItem('user') || '{}');
      userRole = userObj.role || '';
    } catch (e) {}
    
    setRole(userRole);
    if (userRole === 'ORGANISATION') {
      const orgId = localStorage.getItem('ud-user');
      if (orgId) {
        fetchSellers(orgId, 1, '', '', '', 1000).then(res => {
          if (res.sellers) setOrgSellers(res.sellers);
        }).catch(err => console.error(err));
      }
    }
  }, []);

  const isAllSelected = orders.length > 0 && orders.every(order => 
    selectedOrder.some(obj => obj._id === order._id)
  );

  const isIndeterminate = selectedOrder.length > 0 && !isAllSelected;

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('ud-user') ?? '';
      if (!userId) {
        return window.location.href = `/login`;
      }
      
      const queryParams: any = {
        page: pagination.current,
        limit: pagination.pageSize,
        userId: userId,
      };

      if (searchText) queryParams.search = searchText;
      if (statusFilter !== 'all') queryParams.status = statusFilter;
      if (dateRangeFilter !== 'All Time') queryParams.dateRange = dateRangeFilter;
      if (priceRange[0] > 0) queryParams.minPrice = priceRange[0];
      if (priceRange[1] < 10000) queryParams.maxPrice = priceRange[1];
      if (selectedSeller !== 'all') queryParams.sellerId = selectedSeller;

      const response = await fetchUserOrders({ params: queryParams });
      setOrders(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.total,
      }));
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [pagination.current, pagination.pageSize, statusFilter, searchText, dateRangeFilter, priceRange, selectedSeller]);

  const handleTableChange = (newPagination: any) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const handleSelectAll = (e: any) => {
    if (e.target.checked) {
      const newSelections = orders.filter(
        obj => !selectedOrder.some(order => order._id === obj._id)
      );
      setSelectedOrder([...selectedOrder, ...newSelections]);
    } else {
      setSelectedOrder(selectedOrder.filter(
        obj => !orders.some(order => order._id === obj._id)
      ));
    }
  };

  const columns: ColumnsType<NewOrder> = [
    {
      title: <input type="checkbox" checked={isAllSelected} ref={input => { if (input) input.indeterminate = isIndeterminate; }} onChange={handleSelectAll} />,
      dataIndex: 'checkbox',
      key: 'checkbox',
      render: (_, record) => (
        <input type="checkbox" checked={selectedOrder.some(obj => obj._id === record._id)} onChange={() => checkOrderDetails(record)} />
      ),
    },
    { title: 'Order ID', dataIndex: 'orderId', key: 'orderId' },
    { title: 'Customer Name', dataIndex: 'customerName', key: 'customerName' },
    { title: 'Date', dataIndex: 'date', key: 'date', render: (date: string) => new Date(date).toLocaleDateString() },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (amount: number) => `₹${amount}` },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let color = 'default';
        if (status === 'Pending') color = 'orange';
        if (status === 'Accepted') color = 'blue';
        if (status === 'In-progress') color = 'purple';
        if (status === 'Completed') color = 'green';
        if (status === 'Cancelled') color = 'red';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button type="link" onClick={() => viewOrderDetails(record._id)}>
          View
        </Button>
      ),
    },
  ];

  const viewOrderDetails = (orderId: string) => {
    window.location.href = `/order/details/${orderId}`;
  };

  const checkOrderDetails = (order: any) => {
    if (selectedOrder.find(obj => obj._id == order._id)) {
      setSelectedOrder(selectedOrder.filter(obj => obj._id != order._id))
    } else {
      setSelectedOrder([order, ...selectedOrder]);
    }
  }

  const downloadLabel = () => {
    selectedOrder.forEach(obj => {
      if (obj.shippingLabelUrl) {
        window.open(obj?.shippingLabelUrl)
      }
    })
    setSelectedOrder([]);
  }

  const downloadZip = async () => {
    const items = selectedOrder
      .map((obj, idx) => {
        const url = obj.shippingLabelUrl;
        const orderId = obj?.orderId || `order_${idx + 1}`;
        return url ? { url, orderId } : null;
      })
      .filter(Boolean);
  
    if (items.length === 0) return;
    setLoading(true);
    try {
      const res: any = await getLabelZip(items);
      const blob = new Blob([res.data], { type: "application/zip" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `shipping_labels_${Date.now()}.zip`;
      link.click();
      URL.revokeObjectURL(link.href);
      setSelectedOrder([]);
    } catch (err) {
      console.error("Failed to download zip:", err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    setStatusFilter(localStatus);
    setDateRangeFilter(localDateRange);
    setPriceRange(localPriceRange);
    setSelectedSeller(localSeller);
    setShowFilters(false);
    setPagination(p => ({ ...p, current: 1 })); // reset to page 1
  };

  const hasActiveFilters = statusFilter !== 'all' || dateRangeFilter !== 'All Time' || priceRange[0] > 0 || priceRange[1] < 10000 || selectedSeller !== 'all';

  return (
    <div className="container mx-auto p-4 relative">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold">Order Management</h1>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={downloadLabel}>Download Shipping Label</Button>
          <Button onClick={downloadZip}>Download Zip File</Button>
          <Button
            onClick={() => setIsExportModalOpen(true)}
            className="flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export Excel
          </Button>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search orders..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchText}
            onChange={e => {
                setSearchText(e.target.value);
                setPagination(p => ({ ...p, current: 1 }));
            }}
          />
        </div>
        <button
          onClick={() => {
            setLocalStatus(statusFilter);
            setLocalDateRange(dateRangeFilter);
            setLocalPriceRange(priceRange);
            setLocalSeller(selectedSeller);
            setShowFilters(true);
          }}
          className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <FilterOutlined className="mr-2" />
          Filters
          {hasActiveFilters && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Active
            </span>
          )}
        </button>
      </div>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="_id"
          pagination={pagination}
          onChange={handleTableChange}
        />
      </Spin>

      {/* Filter Modal */}
      {showFilters && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowFilters(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-title">
                      Filter Orders
                    </h3>
                    <div className="space-y-4">
                      {/* Status Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select
                          className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                          value={localStatus}
                          onChange={(e) => setLocalStatus(e.target.value)}
                        >
                          <option value="all">All Statuses</option>
                          <option value="Pending">Pending</option>
                          <option value="Accepted">Accepted</option>
                          <option value="In-progress">In-progress</option>
                          <option value="Completed">Completed</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </div>

                      {/* Date Range Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                        <select
                          className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                          value={localDateRange}
                          onChange={(e) => setLocalDateRange(e.target.value)}
                        >
                          <option value="All Time">All Time</option>
                          <option value="Last 7 Days">Last 7 Days</option>
                          <option value="Last 30 Days">Last 30 Days</option>
                          <option value="This Quarter">This Quarter</option>
                          <option value="Year to Date">Year to Date</option>
                        </select>
                      </div>

                      {/* Price Range Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Order Value: ₹{localPriceRange[0]} - ₹{localPriceRange[1]}
                        </label>
                        <div className="flex items-center space-x-4 pt-2">
                          <input
                            type="range"
                            min="0"
                            max="10000"
                            step="100"
                            value={localPriceRange[0]}
                            onChange={(e) => setLocalPriceRange([parseInt(e.target.value), localPriceRange[1]])}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          />
                          <input
                            type="range"
                            min="0"
                            max="10000"
                            step="100"
                            value={localPriceRange[1]}
                            onChange={(e) => setLocalPriceRange([localPriceRange[0], parseInt(e.target.value)])}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          />
                        </div>
                      </div>

                      {/* Seller Filter */}
                      {role === 'ORGANISATION' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Seller</label>
                          <SearchableSellerDropdown
                            sellers={orgSellers}
                            value={localSeller}
                            onChange={(val) => setLocalSeller(val)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-200">
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        exportType="orders"
        orgSellers={orgSellers}
      />
    </div>
  );
};

export default OrderListPage;
